import {sigArm, sigExposure, sigHasArm} from '../../global/sig';
import {isMyFramesPage} from '../../checkout/ab-redesign';
import {Portal} from '../../util';
import {createDefaultProductGlass} from '../product-init/product-init-state';

declare var __is_production: string | undefined;

/**
 * Statsig experiment for the glass option boxes on the product page; product.cshtml puts the arm in __sig_ab without an
 * exposure (services.SIG.Main.WithGlassOptionsArm). vid 1 = boxes with benefit lines, the "Most chosen upgrade" tag and
 * the glare comparison photo (Standard or, while it is selected, Conservation UV beside the reflection option).
 */
export const glassOptionsKey = 'q4_2026_glass_options';

/** Benefit line per glass id, word for word from WEB-1192's doc (id 2 is not in the doc: its line follows the modal's copy). */
export const GLASS_BENEFITS: Record<number, string> = {
    1: 'Blocks 45% of UV light.',
    2: 'Blocks 45% of UV light. Etched matte finish reduces glare.',
    3: 'Blocks up to 99% of UV light. Helps protect against fading.',
    4: 'Blocks up to 99% of UV light. Etched matte finish reduces glare.',
};

/** The tag and the only option that carries it. Never "Most popular": 78% of frames get standard glass. */
export const GLASS_TAG = 'Most chosen upgrade';
export const GLASS_TAG_ID = 4;

/** CDN path (without extension) of the glare comparison photo; .webp and .jpg exist. Marketing's final photo replaces this one line (crop numbers: ab-glass-options.scss). */
export const GLASS_PHOTO = 'images/cached/general/glass-46';

/**
 * Left-hand side of the photo: Conservation UV (3) while it is the selected glass, otherwise Standard (1). glass-46 has both
 * quadrants (Standard top left, Conservation UV bottom left), so the panel moves the crop to the selected one
 * (ab-glass-options.scss, .uv) and the chip names it.
 */
export function glassPhotoLeftId(selectedId: number): number {
    return selectedId === 3 ? 3 : 1;
}

/** The product flags the offer rule reads (ViewProduct carries them all). */
type GlassFlags = {IsCAF: boolean; AllowGlassID2: number; AllowGlassID3: number; AllowGlassID4: number};

/** The glass ids the product offers, the same rule as the control's filter in glass.tsx: 1 always; 2, 3, 4 when AllowGlassIDn > 0; CAF offers every id but 2. */
export function offeredGlassIds(prod: GlassFlags): number[] {
    return [1, 2, 3, 4].filter(id => (prod.IsCAF && id !== 2) || id === 1 || (id === 2 && prod.AllowGlassID2 > 0) || (id === 3 && prod.AllowGlassID3 > 0) || (id === 4 && prod.AllowGlassID4 > 0));
}

/** The glass title (createDefaultProductGlass): the doc's labels. */
export function glassTitle(id: number): string {
    const glass = createDefaultProductGlass().find(g => g.id === id);
    return glass ? glass.title : '';
}

/** Right-hand photo label: the reflection-control option on offer (4 before 2); null when none is, and then no photo shows. */
export function glassPhotoRightLabel(offered: number[]): string | null {
    if (offered.includes(4)) return glassTitle(4);
    if (offered.includes(2)) return glassTitle(2);
    return null;
}

/** True when this browser is in the test, either arm. MyFrames accounts and framerly.com are not part of it. */
export function inGlassOptionsTest(): boolean {
    return !isMyFramesPage() && Portal?.PortalID !== 3 && sigHasArm(glassOptionsKey);
}

/** True when this browser gets the boxes. */
export function isGlassOptionsVariant(): boolean {
    return inGlassOptionsTest() && sigArm(glassOptionsKey) === 1;
}

/**
 * Records the exposure where the change renders: the glass section with more than one option, in both arms. Production
 * only; the server keeps it to once per browser per 24 hours.
 */
export function logGlassOptionsExposure(): boolean {
    try {
        if (typeof __is_production === 'undefined' || __is_production !== '1') return false;
        return inGlassOptionsTest() && sigExposure(glassOptionsKey);
    } catch {
        return false;
    }
}
